import { createContext, useContext, useState, useCallback } from 'react';

/**
 * @typedef {'error' | 'warning' | 'info' | 'success'} ErrorType
 * @typedef {Object} ErrorDetails
 * @property {string} message - The error message
 * @property {ErrorType} type - The type of error
 * @property {string} [code] - Error code for tracking
 * @property {Object} [details] - Additional error details
 * @property {Date} timestamp - When the error occurred
 */

const ErrorContext = createContext(null);

export const ErrorProvider = ({ children }) => {
  const [error, setError] = useState(null);

  const showError = useCallback((message, type = 'error', code = '', details = {}) => {
    setError({
      message,
      type,
      code,
      details,
      timestamp: new Date()
    });

    if (process.env.NODE_ENV === 'development') {
      const header = type === 'success' ? 'Success Details' : 'Error Details';
      const logMethod = type === 'success' ? console.log : console.error;

      console.group(header);
      logMethod('Message:', message);
      logMethod('Type:', type);
      logMethod('Code:', code);
      logMethod('Details:', details);
      logMethod('Timestamp:', new Date().toISOString());
      console.groupEnd();
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <ErrorContext.Provider value={{ error, showError, clearError }}>
      {children}
    </ErrorContext.Provider>
  );
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};